import './WidgetTemplateEditForm.css';

import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import useGetProducts from '../../b2b-users/hooks/useGetProducts';
import B2BFormContainer from '../../b2b-common/B2BFormContainer';
import { useEffect, useState } from 'react';
import ConfirmationDialog from '../../b2b-common/ConfirmationDialog';
import { Box } from '@mui/material';
import LabelTranslated, { setFieldAndLabelField } from '../../b2b-languages/components/LabelTranslated';
import { useLoginContext } from '../../../App';
import EditText from '../../b2b-common/EditText';
import BooleanToggleField from '../../b2b-common/BooleanToggleField';
import { generateSelectField } from '../../b2b-common/helpers';
import LicenseProductsList, { netProductsAcronyms } from '../../b2b-users/components/licenses/LicenseProductsList';
import useWidgetTemplates from '../hooks/useWidgetTemplates';
import { useContentContext } from '../../layout/Layout';
import { showNotification } from '../../b2b-common/Notification';
import { getSelectedProducts } from '../../b2b-social/forms/SocialTemplateEditForm';

const WidgetTemplateEditForm = ({ template, onSave, onDelete, onCancel }) => {
    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();
    const { t } = useB2BTranslation('b2b-reporting');
    const { productsData, fetchingProductsData } = useGetProducts();
    const { newWidgetTemplate, updateTheWidgetTemplate, deleteTheWidgetTemplate } = useWidgetTemplates();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const blankTemplate = {
        id: 0,
        name: '{widtet.template.NEW-ACRONYM.name}',
        name_label: '{widtet.template.NEW-ACRONYM.name}',
        acronym: 'NEW-ACRONYM',
        description: '{widtet.template.NEW-ACRONYM.description}',
        description_label: '{widtet.template.NEW-ACRONYM.description}',
        scope: 'AREA_INDICATOR',
        isEnabled: false,
        templateRequiredProducts: [],
    };

    const [currentTemplate, setCurrentTemplate] = useState(template ? template : blankTemplate);
    const [productsInCurrentTemplate, setProductsInCurrentTemplate] = useState([]);

    useEffect(() => {
        const getProductsInTemplate = async () => {
            if (productsData && template) {
                const productsInTemplate = getSelectedProducts(productsData, template.templateRequiredProducts);
                // console.log('Products in template:', productsInTemplate);
                setProductsInCurrentTemplate(productsInTemplate);
            } else {
                setProductsInCurrentTemplate([]);
            }
        }
        getProductsInTemplate();
        setCurrentTemplate(template ? template : blankTemplate);        
    }, [template, productsData]);

    const widgetTemplateScopeOptions = [
        { value: 'ORGANIZATION', label: t('Organization') },
        { value: 'PLAN', label: t('Plan') },
        { value: 'AREA', label: t('Area') },
        { value: 'AREA_INDICATOR', label: t('Area Indicator') },
    ];

    const handleSaveTemplate = async () => {
        const toSaveUpdateTemplate = {
            ...currentTemplate,
            name: currentTemplate.name_label,
            description: currentTemplate.description_label,
        };
        if (!template) { // New template
            try {
                let newTemplate = await newWidgetTemplate(toSaveUpdateTemplate);
                showNotification(setNotification, t('Template created successfully'), 'success', 3000);
                onSave && onSave(newTemplate);
            } catch (error) {
                console.error('Failed to create template:', error.message);
                showNotification(setNotification, t('Failed to create template: ') + error.message, 'error', 5000);
            }
        } else { // Update template
            try {
                let updatedTemplate = await updateTheWidgetTemplate(toSaveUpdateTemplate);
                showNotification(setNotification, t('Template updated successfully'), 'success', 3000);
                onSave && onSave(updatedTemplate);
            } catch (error) {
                console.error('Failed to update template:', error.message);
                showNotification(setNotification, t('Failed to update template: ') + error.message, 'error', 5000);
            }
        }
    }

    const handleDeleteTemplate = async () => {
        try {
            await deleteTheWidgetTemplate(currentTemplate.id);
            showNotification(setNotification, t('Template deleted successfully'), 'success', 3000);
            onDelete && onDelete(currentTemplate);
        } catch (error) {
            console.error('Failed to delete template:', error.message);
            showNotification(setNotification, t('Failed to delete template: ') + error.message, 'error', 5000);
        }
    }

    const handleCancel = () => {
        onCancel && onCancel();
    }

    const handleTemplateProductsChange = (products) => {
        const netProductsAcronms = netProductsAcronyms(products);
        setCurrentTemplate({ ...currentTemplate, templateRequiredProducts: netProductsAcronms });
    }

    return (
        <B2BFormContainer
            title={!template ? t('New template') : t('Edit template')}
            buttonsConfig={[
                { label: t('Save'), onClick: handleSaveTemplate },
                { label: t('Delete'), onClick: () => setShowDeleteConfirmation(true) },
                { label: t('Cancel'), onClick: handleCancel },
            ]}
            showDivider={false}
            className={'widget-template-edit-form'}
        >
            <Box display={'flex'} flexDirection={'row'} gap={2} alignItems={'flex-start'}>
                <Box width={'50%'} display={'flex'} flexDirection={'column'} gap={2}>
                    <Box display={'flex'} flexDirection={'row'} gap={2} width={'100%'}>
                        <EditText object={currentTemplate} objectField='acronym'
                            setObjectField={setCurrentTemplate} label={t('Template Acronym')}
                        />
                        <BooleanToggleField label={t('Enabled')} field='isEnabled'
                            object={currentTemplate} setObject={setCurrentTemplate}
                            className={'widget-template-enable-toggle'}
                        />
                        {generateSelectField(widgetTemplateScopeOptions,
                            {
                                label: t('Scope'),
                                value: currentTemplate.scope,
                                onChange: (e) => setCurrentTemplate({ ...currentTemplate, scope: e.target.value }),
                                className: 'widget-template-scope-select',
                            }
                        )}
                    </Box>
                    <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'}>
                        <LabelTranslated field={t('Template name')} labelValue={currentTemplate.name_label}
                            locale={`${t('Template Name')} - ${loginData.locale}`} translation={currentTemplate.name}
                            className={'widget-template-name'}
                            onChange={(event) => {
                                setFieldAndLabelField(currentTemplate, setCurrentTemplate, 'name', event.target.value);
                            }}
                        />
                    </Box>
                    <LabelTranslated field={t('Template Description')} labelValue={currentTemplate.description_label}
                        locale={`${t('Template Description')} - ${loginData.locale}`} translation={currentTemplate.description}
                        rowsNumber={4}
                        className={'widget-template-description'}
                        onChange={(event) => {
                            setFieldAndLabelField(currentTemplate, setCurrentTemplate, 'description', event.target.value);
                        }}
                    />
                </Box>
                <Box width={'50%'} display={'flex'} flexDirection={'column'} gap={2} boxSizing={'border-box'}>
                    <LicenseProductsList
                        title={t('Products in this template')}
                        className="widget-template-products-list"
                        productsData={productsData}
                        includedProducts={productsInCurrentTemplate}
                        isFetching={fetchingProductsData}
                        onProductsChange={handleTemplateProductsChange}
                    />
                </Box>
            </Box>
            <ConfirmationDialog
                title={t('Removal Confirmation Request')}
                mainText={t('Please confirm you want to delete this template')}
                open={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteTemplate}
            />
        </B2BFormContainer>
    );
}

export default WidgetTemplateEditForm;